import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Brain, Sparkles, Lock, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  preference: "Preferences (theme, aspect ratio, favorite model)",
  brand: "Brand details (name, colors, fonts, logo)",
  language: "Preferred language",
  style: "Writing & design style",
  workflow: "Repeated workflows",
  activity: "Activity summaries",
  instruction: "Persistent instructions",
};

/**
 * First-time onboarding modal for AlphaCore AI memory.
 * Shown once when memory_enabled === null (never onboarded).
 */
export default function MemoryOnboardingModal() {
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState({
    preference: true, brand: true, language: true, style: true,
    workflow: true, activity: true, instruction: true,
  });

  useEffect(() => {
    let mounted = true;
    api.get("/memory/settings").then((r) => {
      if (!mounted) return;
      if (r.data?.onboarded === false) setOpen(true);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const submit = async (choice) => {
    setBusy(true);
    try {
      await api.post("/memory/onboard", {
        choice,
        categories: choice === "customize" ? cats : undefined,
      });
      setOpen(false);
      if (choice === "enable" || choice === "customize") {
        toast.success("Memory enabled. AlphaCore AI will start personalizing.");
      } else {
        toast.success("Memory kept off. You can turn it on in Settings any time.");
      }
    } catch (e) {
      toast.error("Could not save your choice. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg" data-testid="memory-onboarding-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10">
              <Brain className="h-5 w-5" style={{ color: "rgb(var(--ac-purple))" }} />
            </div>
            <div>
              <div className="kr-overline" style={{ color: "rgb(var(--ac-purple))" }}>[ ALPHACORE AI ]</div>
              <DialogTitle className="font-display text-2xl mt-1">Meet your memory.</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {!customize ? (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-white/70 leading-relaxed">
              AlphaCore AI can remember your preferences, projects, brand details and workflows —
              so each conversation continues where the last one ended.
            </p>
            <ul className="text-sm text-white/60 space-y-1.5">
              <li className="flex items-start gap-2"><Sparkles className="h-4 w-4 mt-0.5" style={{ color: "rgb(var(--ac-pink))" }} /> Personalized replies across every module</li>
              <li className="flex items-start gap-2"><Sparkles className="h-4 w-4 mt-0.5" style={{ color: "rgb(var(--ac-blue))" }} /> Continue projects without re-explaining context</li>
              <li className="flex items-start gap-2"><Lock className="h-4 w-4 mt-0.5" style={{ color: "rgb(var(--ac-green))" }} /> You can pause, edit, export or delete memory at any time</li>
            </ul>
            <div className="text-xs text-white/40 border-t border-white/5 pt-3">
              Only meaningful facts are saved (preferences, brand, style, workflows). Casual and one-off
              messages are ignored. You can manage everything in <span className="text-white/70">Settings → Memory</span>.
            </div>
          </div>
        ) : (
          <div className="space-y-2 mt-2 max-h-72 overflow-y-auto pr-1">
            <div className="text-sm text-white/70 mb-2">Choose what AlphaCore AI can remember:</div>
            {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center justify-between p-3 border border-white/5 hover:bg-white/[0.02] rounded-sm cursor-pointer">
                <div className="text-sm">{label}</div>
                <Switch checked={cats[k]} onCheckedChange={(v) => setCats((c) => ({ ...c, [k]: v }))} data-testid={`onboard-cat-${k}`} />
              </label>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          {!customize ? (
            <>
              <Button variant="outline" onClick={() => submit("skip")} disabled={busy} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-onboard-skip">
                Not now
              </Button>
              <Button variant="outline" onClick={() => setCustomize(true)} disabled={busy} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-onboard-customize">
                Customize
              </Button>
              <Button onClick={() => submit("enable")} disabled={busy} className="text-white hover:opacity-90" style={{ background: "rgb(var(--ac-purple))" }} data-testid="memory-onboard-enable">
                Enable memory <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setCustomize(false)} disabled={busy} className="border-white/10 hover:bg-white/5 hover:text-white">
                Back
              </Button>
              <Button onClick={() => submit("customize")} disabled={busy} className="text-white hover:opacity-90" style={{ background: "rgb(var(--ac-purple))" }} data-testid="memory-onboard-customize-save">
                Save & enable
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
